import { ChangeEvent, useMemo } from 'react'

import {
  DateRangePicker,
  Dropdown,
  MultiSelectDropdown,
  Option,
  TextField,
} from '@joor/design-system'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'

import {
  ISO_DATE_TIME_FORMAT,
  formatDate,
  getDateFromFormat,
} from 'utils/dates'
import { formatNameWithCode } from 'utils/formatters'
import { toGlobalId } from 'utils/transformations/graphql'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'
import { getAccountId } from 'store/currentUser/selectors'

import { LoadingOrderDetails } from '../../LoadingOrderDetails'
import { OrderDetailsQuery } from '../../OrderDetailsWidget'
import {
  OrderDetailEditType,
  OrderDetailId,
} from '../../orderDetailsWidget.types'
import {
  FormIds,
  FormValues,
  HandleChangeFunction,
} from '../../orderDetailsWidget.utils'
import DetailFormItem from './DetailFormItem'
import { useOrderDetailsDropdownOptions } from './orderDetailsForm.hooks'
import { OrderDetailsIds } from 'features/Orders/Review/review.ids'

export default function OrderDetailsEditForm({
  details,
  onFormChange,
  values,
  dateFormat,
  testId,
  isBrand,
}: Props): JSX.Element {
  const accountId = useSelector(getAccountId)
  const brandId = isBrand
    ? (toGlobalId('Brand', accountId) ?? '')
    : details?.brand?.id || ''

  const retailerId = details?.retailerProfile?.id ?? ''

  const {
    loadingOptions,
    orderEventsOptions,
    orderTypesOptions,
    warehouseOptions,
    seasonsOptions,
    yearOptions,
  } = useOrderDetailsDropdownOptions({
    brandId,
    retailerId,
    injectableOrderEvent: values[FormIds.OrderEvent],
    isBrand,
  })

  const editModeDetailsList: OrderDetailEditType[] = useMemo(() => {
    if (!details) {
      return []
    }

    const { collections, divisions, deliveryWindow, created } = details

    const linesheetsOptions: Option[] = collections
      ? collections?.edges.map((collection) => ({
          value: collection ? collection?.node.id : '',
          label: collection ? collection?.node.name : '',
        }))
      : []

    const division = divisions?.[0]
      ? formatNameWithCode(divisions[0].name, divisions[0].code)
      : EMPTY_CELL_CONTENT

    /**
     * ITEMS ORDER REFERENCE
     * Order Created Date: 0,
     * Linesheets: 1,
     * Warehouse: 2,
     * Season: 3,
     * Year: 4,
     * DivisionCode: 5,
     * OrderDeliveryWindow: 6,
     * Event: 7,
     * OrderType: 8,
     */

    return [
      {
        id: OrderDetailId.OrderCreatedDate,
        title: 'Order Created Date',
        content: (
          <TextField
            id={OrderDetailsIds.CreatedDateId}
            data-testid={OrderDetailsEditFormTestIds.OrderCreatedDate}
            defaultValue={formatDate(created)}
            disabled
          />
        ),
        ownTooltipText: 'Order created date cannot be changed.',
      },
      {
        id: OrderDetailId.Linesheets,
        title: 'Linesheets',
        content: !linesheetsOptions.length ? (
          EMPTY_CELL_CONTENT
        ) : (
          <MultiSelectDropdown
            id={OrderDetailsIds.LinesheetsId}
            testId={OrderDetailsEditFormTestIds.Linesheets}
            defaultValue={linesheetsOptions}
            onDeselect={() => {}}
            options={linesheetsOptions}
            isDisabled
          />
        ),
      },
      {
        id: OrderDetailId.Warehouse,
        title: 'Warehouse',
        content: (
          <Dropdown
            id={OrderDetailsIds.WarehouseId}
            testId={OrderDetailsEditFormTestIds.Warehouse}
            options={warehouseOptions}
            menuPlacement="bottom"
            defaultValue={values[FormIds.Warehouse]}
            onSelect={({ selected }: { selected: Option }) =>
              onFormChange(FormIds.Warehouse, selected)
            }
            variant={!isBrand ? 'DISABLED' : 'DEFAULT'}
          />
        ),
        isEditable: isBrand,
        ownTooltipText: !isBrand ? 'Warehouse cannot be changed' : '',
      },
      {
        id: OrderDetailId.Season,
        title: 'Season',
        content: (
          <SeasonInputsWrapper>
            <Dropdown
              id={OrderDetailsIds.SeasonNameId}
              testId={OrderDetailsEditFormTestIds.SeasonName}
              options={seasonsOptions}
              menuPlacement="bottom"
              defaultValue={seasonsOptions?.find(
                (season) => season.label === values[FormIds.SeasonName],
              )}
              onSelect={({ selected }: { selected: Option }) => {
                onFormChange(FormIds.SeasonName, selected.label)
              }}
              variant={!isBrand ? 'DISABLED' : 'DEFAULT'}
            />
            <TextField
              id={OrderDetailsIds.SeasonCodeId}
              data-testid={OrderDetailsEditFormTestIds.SeasonCode}
              defaultValue={values[FormIds.SeasonCode] ?? undefined}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onFormChange(FormIds.SeasonCode, e.target.value)
              }
              placeholder="Code"
              disabled={!isBrand}
            />
          </SeasonInputsWrapper>
        ),
        isEditable: isBrand,
        ownTooltipText: !isBrand ? 'Season cannot be changed' : '',
      },
      {
        id: OrderDetailId.Year,
        title: 'Year',
        content: (
          <Dropdown
            id={OrderDetailsIds.YearId}
            testId={OrderDetailsEditFormTestIds.Year}
            options={yearOptions}
            menuPlacement="bottom"
            defaultValue={yearOptions?.find(
              (year) => year.value === values[FormIds.SeasonYear]?.toString(),
            )}
            onSelect={({ selected }: { selected: Option }) =>
              onFormChange(FormIds.SeasonYear, parseInt(selected.value))
            }
            variant={!isBrand ? 'DISABLED' : 'DEFAULT'}
          />
        ),
        isEditable: isBrand,
        ownTooltipText: !isBrand ? 'Year cannot be changed' : '',
      },
      {
        id: OrderDetailId.DivisionCode,
        title: 'Division',
        content: (
          <TextField
            id={OrderDetailsIds.DivisionId}
            data-testid={OrderDetailsEditFormTestIds.Division}
            defaultValue={division}
            disabled
          />
        ),
      },
      {
        id: OrderDetailId.OrderDeliveryWindow,
        title: 'Order Delivery Window',
        content: (
          <DateRangePicker
            startDateAriaLabel="Order Start date"
            endDateAriaLabel="Order End date"
            startDate={
              deliveryWindow?.minStartShipDate
                ? getDateFromFormat(
                    deliveryWindow.minStartShipDate,
                    ISO_DATE_TIME_FORMAT,
                    { utc: true },
                  )
                : null
            }
            startDateId={OrderDetailsIds.OrderDeliveryWindowStartId}
            endDate={
              deliveryWindow?.maxCompleteShipDate
                ? getDateFromFormat(
                    deliveryWindow.maxCompleteShipDate,
                    ISO_DATE_TIME_FORMAT,
                    { utc: true },
                  )
                : null
            }
            endDateId={OrderDetailsIds.OrderDeliveryWindowEndId}
            focusedInput={null}
            onDatesChange={() => {}}
            onFocusChange={() => {}}
            placeholder={dateFormat?.toUpperCase() ?? 'MM/DD/YYYY'}
            displayFormat={dateFormat?.toUpperCase() ?? 'MM/DD/YYYY'}
            disabled
          />
        ),
      },
      {
        id: OrderDetailId.Event,
        title: 'Event',
        content: (
          <Dropdown
            id={OrderDetailsIds.EventId}
            testId={OrderDetailsEditFormTestIds.Event}
            options={orderEventsOptions}
            menuPlacement="top"
            defaultValue={values[FormIds.OrderEvent]}
            onSelect={({ selected }: { selected: Option }) =>
              onFormChange(FormIds.OrderEvent, selected)
            }
            variant={!isBrand ? 'DISABLED' : 'DEFAULT'}
          />
        ),
        isEditable: isBrand,
        ownTooltipText: !isBrand ? 'Event cannot be changed' : '',
      },
      {
        id: OrderDetailId.OrderType,
        title: 'Order Type',
        content: (
          <Dropdown
            id={OrderDetailsIds.OrderTypeId}
            testId={OrderDetailsEditFormTestIds.OrderType}
            options={orderTypesOptions}
            menuPlacement="top"
            defaultValue={values[FormIds.OrderType]}
            onSelect={({ selected }: { selected: Option }) =>
              onFormChange(FormIds.OrderType, selected)
            }
            variant={!isBrand ? 'DISABLED' : 'DEFAULT'}
          />
        ),
        isEditable: isBrand,
        ownTooltipText: !isBrand ? 'Order Type cannot be changed' : '',
      },
    ]
  }, [
    dateFormat,
    details,
    onFormChange,
    orderEventsOptions,
    orderTypesOptions,
    values,
    warehouseOptions,
    isBrand,
    seasonsOptions,
    yearOptions,
  ])

  if (loadingOptions) {
    return <LoadingOrderDetails fieldsAmount={10} />
  }

  return (
    <Container data-testid={testId ?? OrderDetailsEditFormTestIds.Container}>
      {editModeDetailsList.map(
        ({ id, title, content, isEditable, ownTooltipText }) => (
          <DetailFormItem
            key={id}
            title={title}
            content={content}
            isEditable={isEditable}
            ownTooltipText={ownTooltipText}
          />
        ),
      )}
    </Container>
  )
}

export type Props = {
  details: OrderDetailsQuery
  onFormChange: HandleChangeFunction
  values: FormValues
  dateFormat?: string
  testId?: string
  isBrand: boolean
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SeasonInputsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;

  & > div:first-child {
    flex: 1;
  }

  & > div:last-child {
    width: 125px;
  }
`

export const OrderDetailsEditFormTestIds = {
  Container: 'order-details-edit-form-container',
  Linesheets: 'order-details-form-linesheets',
  Warehouse: 'order-details-form-warehouse',
  SeasonName: 'order-details-form-season-name',
  SeasonCode: 'order-details-form-season-code',
  Year: 'order-details-form-year',
  Division: 'order-details-form-division',
  OrderDeliveryWindow: 'order-details-form-order-delivery-window',
  Event: 'order-details-form-event',
  OrderType: 'order-details-form-order-type',
  TrackingNumber: 'order-details-form-tracking-number',
  OrderCreatedDate: 'order-details-created-date',
} as const
